import type { OrderMarginType } from '@flipster/utils/types/base';

import { IOrderLineAdapter } from '../../../../../lib/charting_library/charting_library';
import { ChartStyles } from '../../../../misc/constants';
import TradingView from '../../../../trading-view';
import { drawTpSlArea, removeTpSlArea, type TpSlAreaHandle } from './draggable-tpsl-line-helper/tp-sl-drag-area';
import { isTpSlLiqCross, isTpSlZoneMismatch } from './draggable-tpsl-line-helper/tp-sl-drag-drop';
import { deriveTpSlDragIntent, type TpSlDragIntent } from './draggable-tpsl-line-helper/tp-sl-drag-intent';
import { buildExpectedPnlLabel, tpSlDragLineStyle } from './draggable-tpsl-line-helper/tp-sl-drag-style';

/** 손익 컨텍스트가 있는 라인(=TP/SL)을 드래그할 때, 예상 손익/존을 그리기 위한 값. */
export type PnlContext = {
  /** 부모 포지션 id (loss zone 드롭 시 모달 intent에 필요). */
  positionId: string;
  /** 이 라인의 주문 타입. loss zone 모달을 방향이 아닌 "기존 주문 타입"으로 열기 위해 쓴다. */
  lineType: 'TP' | 'SL';
  positionSide: 'Long' | 'Short';
  /** 포지션 진입가. 손익존/예상손익 계산 및 loss zone 판정의 기준. */
  entryPrice: number;
  /** 주문 체결 수량(절대값). */
  quantity: number;
  /** 포지션 청산가. SL을 청산가 너머로 끌면 모달을 띄워 liq 에러를 보이기 위해 쓴다. */
  liqPrice: number;
  /** 포지션 마진 타입. liq 판정은 Isolated에서만(모달 evaluatePriceCase와 동일). */
  marginType: OrderMarginType | undefined;
};

export type OrderLike = {
  id: string;
  side: 'Long' | 'Short';
  /** Left-half label text for the chart line (e.g. "TP Trigger 1,234 / Market"). */
  label: string;
  /** Pre-formatted quantity string rendered in the line adapter's quantity box. */
  qty: string;
  price: number;
  slot: number;
  /** 있으면 드래그 중 진입가↔드래그가 fill 존 + "Expected profit/loss" 라벨을 그린다 (TP/SL 전용). */
  pnlContext?: PnlContext;
};

export type LineStyleConfig = {
  bodyColor: string;
  lineColor: string;
};

export type DraggableOrderLineCallbacks = {
  onDragComplete: (orderId: string, newPrice: number) => Promise<void>;
  onCancelClick: (orderId: string) => Promise<void>;
  /**
   * 드롭이 "직접 re-price 불가"일 때, 호스트가 해당 가격으로 TP/SL **수정 모달**을 열도록 요청한다
   * (콜백 미주입 시 모달 없이 일반 수정). 차트 패키지는 모달을 직접 열지 않고 이 콜백만 emit한다.
   * 실제 오픈: 데스크톱 web에서 `tv-chart-web`이 이걸 `onOpenTpSlModal`에 연결 → `use-desktop-trade-callbacks`
   * → `ChartTpSlDragOpener`가 그 주문의 수정 모달을 연다.
   */
  onOpenEditModal?: (intent: TpSlDragIntent) => void;
};

/** 드래그 중 정체성 스왑(원위치 고스트 라인 표시)에 필요한 원래 라인 정보. */
type OrderMeta = { label: string; qty: string; side: 'Long' | 'Short' };

class DraggableOrderLineHelper {
  #lines: Record<string, IOrderLineAdapter> = {};
  #originalPrices: Record<string, number> = {};
  #inFlightMutations: Set<string> = new Set();
  #draggingLines: Set<string> = new Set();
  /** TP/SL 라인의 손익 컨텍스트(부모 포지션 진입가/side/수량). 있을 때만 드래그 중 존/라벨을 그린다. */
  #pnlContexts: Record<string, PnlContext> = {};
  /** 드래그 중에만 보이는 손익존 사각형. */
  #areas: Record<string, TpSlAreaHandle> = {};
  /**
   * 드래그 중 "원래 TP/SL 가격"에 표시되는 고정 고스트 라인. 드래그되는 라인이 Expected profit으로
   * 바뀌는 동안, 이 고스트가 TP/SL 라벨을 들고 있어 "TP/SL은 제자리"처럼 보이게 한다.
   */
  #pnlLines: Record<string, IOrderLineAdapter> = {};
  /** 원래 라인 라벨/수량/side (드래그 중 정체성 스왑 및 복원용). */
  #orderMeta: Record<string, OrderMeta> = {};

  constructor(
    private readonly tv: TradingView,
    private readonly getLineStyle: (side: 'Long' | 'Short') => LineStyleConfig,
    private readonly callbacks: DraggableOrderLineCallbacks,
    /** webview면 라인을 비편집으로 둔다(드래그 scope out — × 청산/삭제만 브리지). 데스크톱은 드래그 가능. */
    private readonly isWebview: boolean = false,
  ) {}

  draw(symbol: string, orders: OrderLike[]) {
    if (this.tv.symbol !== symbol) return;
    // Remove lines for orders that no longer exist
    // Skip lines with in-flight mutations to prevent premature removal during delete+create
    Object.keys(this.#lines).forEach((id) => {
      if (!orders.find((o) => o.id === id) && !this.#inFlightMutations.has(id)) {
        this.#remove(id);
      }
    });

    // Sort by slot for consistent stacking order (matches existing OrderLineHelper pattern)
    [...orders].sort((a, b) => a.slot - b.slot).forEach((o) => this.#draw(o));
  }

  /** TP/SL 라인의 평상시(정상) 스타일. */
  #applyNormalStyle(line: IOrderLineAdapter, style: LineStyleConfig) {
    line
      .setLineColor(style.lineColor)
      .setBodyTextColor(ChartStyles.Color.White)
      .setBodyBackgroundColor(style.bodyColor)
      .setBodyBorderColor(style.bodyColor)
      .setQuantityTextColor(ChartStyles.Color.White)
      .setQuantityBackgroundColor(ChartStyles.Color.Background)
      .setQuantityBorderColor(style.bodyColor);
  }

  #draw(order: OrderLike) {
    try {
      if (!this.tv.enabled || !this.tv.activeChart) return;

      // Skip redraw for orders being dragged or with in-flight mutations
      if (this.#draggingLines.has(order.id)) return;
      if (this.#inFlightMutations.has(order.id) && this.#lines[order.id]) return;

      const style = this.getLineStyle(order.side);
      const isNew = !this.#lines[order.id];
      if (isNew) {
        const line = this.tv
          .createOrderLine()
          // webview는 비편집(드래그 scope out), 데스크톱은 상시 드래그 가능. 둘 다 ×(취소)는 동작.
          .setEditable(!this.isWebview)
          .setCancellable(true)
          .setCancelTooltip('Cancel order')
          .setCancelButtonBackgroundColor(ChartStyles.Color.Background)
          .setCancelButtonBorderColor(style.bodyColor)
          .setCancelButtonIconColor(ChartStyles.Color.White)
          .setModifyTooltip('');
        this.#applyNormalStyle(line, style);

        const orderId = order.id;

        line.onMoving(() => {
          this.#draggingLines.add(orderId);
          this.#drawPnl(orderId, line.getPrice());
        });

        line.onMove(() => {
          this.#draggingLines.delete(orderId);
          this.#clearPnl(orderId);
          this.#handleDragComplete(orderId, line);
        });

        line.onCancel(() => {
          this.#handleCancel(orderId);
        });

        this.#lines[order.id] = line;
      }

      this.#lines[order.id].setText(order.label).setPrice(order.price).setQuantity(order.qty);

      // Update original price from server data (for snap-back on failure)
      this.#originalPrices[order.id] = order.price;
      // 손익 컨텍스트/메타(있으면 드래그 중 존·정체성 스왑)를 최신값으로 유지
      this.#orderMeta[order.id] = { label: order.label, qty: order.qty, side: order.side };
      if (order.pnlContext) this.#pnlContexts[order.id] = order.pnlContext;
      else delete this.#pnlContexts[order.id];
    } catch {
      // Value is null error (TradingView internal)
    }
  }

  async #handleDragComplete(orderId: string, line: IOrderLineAdapter) {
    const newPrice = line.getPrice();
    const originalPrice = this.#originalPrices[orderId];
    if (newPrice === originalPrice) return;

    // 무효 드롭이면 수정 모달 오픈을 요청하고 종료, 정상 드롭이면 아래에서 직접 re-price.
    const invalidDropIntent = this.#resolveInvalidDropIntent(orderId, newPrice);
    if (invalidDropIntent && this.callbacks.onOpenEditModal) {
      line.setPrice(originalPrice); // 라인은 원위치 — 실제 변경은 모달이 처리한다
      this.callbacks.onOpenEditModal(invalidDropIntent);
      return;
    }

    // 정상 드롭: 그대로 직접 re-price.
    this.#inFlightMutations.add(orderId);
    try {
      await this.callbacks.onDragComplete(orderId, newPrice);
    } catch {
      if (this.#lines[orderId]) line.setPrice(originalPrice); // 실패 시 원위치로 snap-back
    } finally {
      this.#inFlightMutations.delete(orderId);
    }
  }

  /**
   * 드롭이 "직접 re-price 불가"면 모달에 보낼 intent를, 아니면 null을 반환한다. 두 케이스:
   * ① 주문 타입↔구간 불일치(TP를 손실/SL을 이익 구간), ② SL을 청산가 너머로 드롭.
   * intent의 kind는 방향이 아니라 "기존 주문 타입"(TP/SL)으로, orderId를 실어 보내 모달이
   * 그 주문 수정(entire→entire-update / partial→partial-update)으로 열리게 한다.
   */
  #resolveInvalidDropIntent(orderId: string, newPrice: number): TpSlDragIntent | null {
    const ctx = this.#pnlContexts[orderId];
    const intent = this.#intentFor(orderId, newPrice);
    if (!ctx || !intent) return null;

    const isInvalid =
      isTpSlZoneMismatch(ctx.lineType, intent.kind) ||
      isTpSlLiqCross({
        lineType: ctx.lineType,
        positionSide: ctx.positionSide,
        price: newPrice,
        liqPrice: ctx.liqPrice,
        marginType: ctx.marginType,
      });
    if (!isInvalid) return null;

    return { ...intent, kind: ctx.lineType, orderId };
  }

  async #handleCancel(orderId: string) {
    if (!this.#lines[orderId]) return;

    // 낙관적 제거 안 함: 확인 다이얼로그(호출부 onCancelClick)에서 취소할 수 있으므로 라인을 유지한다.
    // 실제 삭제되면 다음 draw() 사이클(WebSocket 상태)에서 라인이 사라진다.
    try {
      await this.callbacks.onCancelClick(orderId);
    } catch {
      // 삭제 실패/취소 — 라인 그대로 유지
    }
  }

  /** 부모 포지션 진입가 기준 드래그 intent. pnlContext 없으면(=TP/SL 아님) undefined. */
  #intentFor(orderId: string, price: number): TpSlDragIntent | undefined {
    const ctx = this.#pnlContexts[orderId];
    if (!ctx) return undefined;
    return deriveTpSlDragIntent(
      { positionId: ctx.positionId, side: ctx.positionSide, entryPrice: ctx.entryPrice, quantity: ctx.quantity },
      price,
    );
  }

  /**
   * 드래그 중: ① 진입가↔드래그가 fill 존, ② 드래그되는 라인을 "Expected profit/loss"로 변경,
   * ③ 원래 TP/SL 가격엔 고스트 라인을 띄워 TP/SL 라벨을 들고 있게 한다(정체성 스왑).
   */
  #drawPnl(orderId: string, draggedPrice: number) {
    const ctx = this.#pnlContexts[orderId];
    const meta = this.#orderMeta[orderId];
    const draggedLine = this.#lines[orderId];
    // 진입가 기준: 이익 방향이면 녹색(TP), 손실 방향이면 붉은색(SL).
    const intent = this.#intentFor(orderId, draggedPrice);
    if (!ctx || !meta || !draggedLine || !intent) return; // TP/SL이 아니면(컨텍스트 없음) 스왑/존 없음

    // ① 손익존: 기존 TP/SL 가격 ↔ 드래그가 (포지션 라인이 아니라 TP/SL 라인부터 채운다)
    const area = drawTpSlArea(this.tv, this.#originalPrices[orderId], draggedPrice, intent.kind, this.#areas[orderId]);
    if (area) this.#areas[orderId] = area;
    else delete this.#areas[orderId];

    // ② 드래그되는 라인 = Expected profit/loss
    const label = buildExpectedPnlLabel(intent.expectedPnl, this.tv.quoteCurrency);
    const dStyle = tpSlDragLineStyle(intent.kind);
    try {
      // 드래그 중 "Expected ..." 표시엔 ×(취소) 버튼을 숨긴다.
      // setCancellable(false)가 드래그 중 즉시 리렌더 안 될 수 있어 버튼 색도 투명 처리한다.
      draggedLine
        .setCancellable(false)
        .setCancelButtonBackgroundColor('rgba(0, 0, 0, 0)')
        .setCancelButtonBorderColor('rgba(0, 0, 0, 0)')
        .setCancelButtonIconColor('rgba(0, 0, 0, 0)')
        .setText(label.text)
        .setQuantity(label.value)
        .setLineColor(dStyle.lineColor)
        .setBodyBackgroundColor(dStyle.bodyBackgroundColor)
        .setBodyBorderColor(dStyle.bodyBorderColor)
        .setBodyTextColor(dStyle.bodyTextColor)
        .setQuantityBackgroundColor(dStyle.quantityBackgroundColor)
        .setQuantityBorderColor(dStyle.quantityBorderColor)
        .setQuantityTextColor(dStyle.quantityTextColor);
    } catch {
      // teardown 중 null 에러
    }

    // ③ 원래 가격 고정 고스트 = TP/SL 라벨 (시각적으로 'TP/SL은 여기')
    let ghost = this.#pnlLines[orderId];
    if (!ghost) {
      try {
        ghost = this.tv.createOrderLine().setEditable(false);
        this.#pnlLines[orderId] = ghost;
      } catch {
        return; // teardown 중 null 에러
      }
    }
    try {
      ghost.setPrice(this.#originalPrices[orderId]).setText(meta.label).setQuantity(meta.qty);
      this.#applyNormalStyle(ghost, this.getLineStyle(meta.side));
    } catch {
      // teardown 중 null 에러
    }
  }

  /** 드래그 종료/취소 시: 손익존·고스트 제거하고 드래그되던 라인을 원래 TP/SL 외형으로 복원한다. */
  #clearPnl(id: string) {
    removeTpSlArea(this.tv, this.#areas[id]);
    delete this.#areas[id];
    this.#pnlLines[id]?.remove();
    delete this.#pnlLines[id];

    const line = this.#lines[id];
    const meta = this.#orderMeta[id];
    if (line && meta) {
      try {
        const style = this.getLineStyle(meta.side);
        this.#applyNormalStyle(line, style);
        // × 복원: cancellable + 버튼 색(생성 시와 동일)
        line
          .setCancellable(true)
          .setCancelButtonBackgroundColor(ChartStyles.Color.Background)
          .setCancelButtonBorderColor(style.bodyColor)
          .setCancelButtonIconColor(ChartStyles.Color.White)
          .setText(meta.label)
          .setQuantity(meta.qty);
      } catch {
        // teardown 중 null 에러
      }
    }
  }

  #remove(id: string) {
    removeTpSlArea(this.tv, this.#areas[id]);
    delete this.#areas[id];
    this.#pnlLines[id]?.remove();
    delete this.#pnlLines[id];
    this.#lines[id]?.remove();
    delete this.#lines[id];
    delete this.#originalPrices[id];
    delete this.#pnlContexts[id];
    delete this.#orderMeta[id];
    this.#draggingLines.delete(id);
  }

  clean() {
    Object.keys(this.#lines).forEach((id) => this.#remove(id));
  }
}

export default DraggableOrderLineHelper;
